import { useState, useEffect, useRef } from "react";
import Modal from "./Modal";
import '../styles/Customs.css';
import { Boolean, Contact, Dropdown, Text, NumberInput, DateInput, Checkbox } from "./FieldComponents";
import { ChevronDown, ChevronUp, Info, Pencil, Send, SquarePen } from "lucide-react";
import { useNavVariant } from "../App";
import PublicFormRequestFieldSettings from './PublicFormRequestFieldSettings';
import {
    createPublicFormRequestFieldConfig,
    isPublicFormRequestFieldType,
    parsePublicFormRequestFieldConfig
} from '../utils/matterPublicForms';

export const CustomFields = () => {
    const [createNew, setCreateNew] = useState(null);
    const [editingField, setEditingField] = useState(null);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const navVariant = useNavVariant();
    const [fields, setFields] = useState([]);
    const [caseTypes, setCaseTypes] = useState([]);
    const [fieldTypes, setFieldTypes] = useState([]);
    const [newField, setNewField] = useState({
        name: "",
        field_id: 0,
        case_type_id: 0,
        required: false,
        options: [],
        obsolete: 0,
        hidden: 0,
        add_item: null,
        default_value: "",
        display_when: null,
        is_answered: "",
        is_answer_negated: false,
        section_id: null,
        template_id: 1
    });    
    const [selectedFieldType, setSelectedFieldType] = useState(null);
    const filteredFields = fields.filter(f => (
        f.case_type_id === Number(newField.case_type_id)
        && !isPublicFormRequestFieldType(f.field_id)
    ));
    const isPublicFormRequestSelection = isPublicFormRequestFieldType(selectedFieldType)
        || isPublicFormRequestFieldType(newField.field_id);
    const selectedDisplayField = fields.find(f => String(f.id) === String(newField.display_when));
    const displayFieldOptions = Array.isArray(selectedDisplayField?.options) 
        ? selectedDisplayField.options
        : [];
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    const [fieldOptionsSelected, setFieldOptionsSelected] = useState({});
    const [activeCaseType, setActiveCaseType] = useState(null);
    const [sections, setSections] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [fieldSaveError, setFieldSaveError] = useState('');
    const hintRef = useRef(null);

    useEffect(() => {
        const foundType = fieldTypes.find(ft => Number(ft.id) === Number(newField.field_id));
        setSelectedFieldType(foundType ? foundType.type : null);

        // Only overwrite options when they are empty/null to avoid clearing existing values on edit
        if (isPublicFormRequestFieldType(foundType?.type) || isPublicFormRequestFieldType(foundType?.id)) {
            setNewField(prev => ({
                ...prev,
                required: false,
                add_item: false,
                default_value: '',
                display_when: null,
                is_answered: '',
                is_answered_negated: false,
                is_answer_negated: false,
                options: prev.options && !Array.isArray(prev.options)
                    ? parsePublicFormRequestFieldConfig(prev.options)
                    : createPublicFormRequestFieldConfig()
            }));
        } else if (foundType?.type === 'boolean') {
            setNewField(prev => ({
                ...prev,
                options: (Array.isArray(prev.options) && prev.options.length > 0)
                    ? prev.options
                    : ["Yes", "No", "Unknown"]
            }));
        } else if (fieldTypeNeedsOptions(foundType?.type)) {
            setNewField(prev => ({
                ...prev,
                options: (Array.isArray(prev.options) && prev.options.length > 0)
                    ? prev.options
                    : []
            }));
        }
    }, [newField.field_id, fieldTypes]);

    const fieldTypeNeedsOptions = (fieldType) => {
        return ["list", "multiselect", "searchselect", "rating"].includes(fieldType);
    };
    
    const isBooleanType = (fieldType) => {
        return fieldType === "boolean";
    };

    const handleAddOption = () => {
        setNewField((prev) => ({
            ...prev,
            options: [...prev.options, ""]
        }));
    };
    
    const handleOptionChange = (index, value) => {
        const updatedOptions = [...newField.options];
        updatedOptions[index] = value;
        setNewField((prev) => ({
            ...prev,
            options: updatedOptions
        }));
    };
    
    const handleRemoveOption = (index) => {
        const updatedOptions = [...newField.options];
        updatedOptions.splice(index, 1);
        setNewField((prev) => ({
            ...prev,
            options: updatedOptions
        }));
    };

    const fetchAllData = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/custom_fields.php?schema_admin=1&time=${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();
    
            if (data.success) {
                const parsedFields = data.custom_fields.map(fields => ({
                    ...fields,
                    options: fields.options ? JSON.parse(fields.options) : []
                }));
    
                setCaseTypes(data.case_types.filter(type => type.id !== "0"));
                setFields(parsedFields);
                setFieldTypes(data.fields);
            } else {
                console.error("Error fetching data:", data.message);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const fetchTemplates = async () => {
        try {
            const response = await fetch('https://api.casedb.co/layout.php');
            const data = await response.json();
            if (data.success) {
                setTemplates(data.templates);
            } else {
                console.error("Error fetching templates:", data.message);
            }
        } catch (error) {
            console.error("Error fetching templates:", error);
        }
    };

    const fetchSections = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/sections.php?template_id=${newField.template_id}`);
            const data = await response.json();
            if (data.success) {
                setSections(data.sections);
            } else {
                console.error("Error fetching sections:", data.message);
            }
        } catch (err) {
            console.error("Error fetching sections:", err);
        }
    };
    
    useEffect(() => {
        document.title = "Custom Fields | CaseDB";
        
        fetchAllData();
        fetchTemplates();
    }, []);

    useEffect(() => {
        if (newField.template_id > 0) {
            fetchSections();
        };
    }, [newField.template_id]);

    const createCaseType = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/case_types.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(newField)
            });
            const data = await response.json();
            if (data.success) {
                setCaseTypes([...caseTypes, newField]);
                setNewField({
                    name: "",
                });
            } else {
                console.error("Error creating new case type:", data.message);
            }
        } catch (error) {
            console.error("Error creating new case type:", error);
        }
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewField(prevState => ({
            ...prevState,
            [name]: value === "" ? "" : value
        }));
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setNewField(prevState => ({
            ...prevState,
            [name]: checked
        }));
    };

    const handleFieldSubmit = async () => {
        const publicFormConfig = parsePublicFormRequestFieldConfig(newField.options);
        const isPublicFormRequest = isPublicFormRequestSelection;
        if (isPublicFormRequest && !publicFormConfig.form_id) {
            setFieldSaveError('Choose a published public form for this field.');
            return false;
        }
        if (isPublicFormRequest && publicFormConfig.default_channels.length === 0) {
            setFieldSaveError('Choose email, text message, or both.');
            return false;
        }
        setFieldSaveError('');
        try {
            const hasOptions = Array.isArray(newField.options)
                ? newField.options.length > 0
                : newField.options && typeof newField.options === 'object' && Object.keys(newField.options).length > 0;
            const payload = {
                ...newField,
                ...(isPublicFormRequest ? {
                    required: false,
                    add_item: false,
                    default_value: '',
                    display_when: null,
                    is_answered: '',
                    is_answered_negated: false,
                    is_answer_negated: false,
                } : {}),
                options: isPublicFormRequest
                    ? publicFormConfig
                    : hasOptions ? newField.options : null,
            };
    
            const response = await fetch(`https://api.casedb.co/custom_fields.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
                credentials: 'include'
            });
    
            const data = await response.json();
            if (data.success) {
                fetchAllData();
                setCreateNew(false);
                setNewField({
                    name: "",
                    field_id: 0,
                    case_type_id: 0,
                    required: false,
                    options: [],
                    obsolete: 0,
                    hidden: 0,
                    add_item: null,
                    default_value: "",
                    display_when: null,
                    is_answered: "",
                    is_answer_negated: false,
                    section_id: null,
                    template_id: 1
                });
                return true;
            } else {
                console.error("Error creating custom field:", data.message);
                setFieldSaveError(data.message || 'Failed to create custom field.');
            }
        } catch (error) {
            console.error("Error creating custom field:", error);
            setFieldSaveError(error.message || 'Failed to create custom field.');
        }
        return false;
    };

    const handleMouseMove = (event) => {
        setTooltipPos({ x: event.clientX + 10, y: event.clientY });
    };

    const handleBooleanChange = (field_id, value) => {
        setFieldOptionsSelected((prev) => ({
            ...prev,
            [field_id]: value
        }));
    };

    const resetForm = () => {
        setNewField({
            name: "",
            field_id: 0,
            case_type_id: 0,
            required: false,
            options: [],
            obsolete: 0,
            hidden: 0,
            add_item: null,
            default_value: "",
            display_when: null,
            is_answered: "",
            is_answer_negated: false,
            section_id: null,
            template_id: 1
        });
        setEditingField(null);
        setFieldSaveError('');
    };

    const handleEditField = (field) => {
        // Parse the existing field data to populate the edit form
        let parsedOptions = [];
        try {
            parsedOptions = Array.isArray(field.options) 
                ? field.options 
                : field.options && typeof field.options === 'object'
                    ? field.options
                    : field.options ? JSON.parse(field.options) : [];
        } catch (error) {
            console.error('Error parsing field options:', error);
            parsedOptions = [];
        }

        let parsedSectionId = null;
        try {
            const sectionIds = Array.isArray(field.section_id) 
                ? field.section_id 
                : field.section_id ? JSON.parse(field.section_id) : [];
            parsedSectionId = sectionIds[0] || null;
        } catch (error) {
            console.error('Error parsing section_id:', error);
            parsedSectionId = null;
        }

        let parsedTemplateId = 1;
        try {
            const templateIds = Array.isArray(field.template_ids) 
                ? field.template_ids 
                : field.template_ids ? JSON.parse(field.template_ids) : [1];
            parsedTemplateId = templateIds[0] || 1;
        } catch (error) {
            console.error('Error parsing template_ids:', error);
            parsedTemplateId = 1;
        }

        setNewField({
            name: field.name || "",
            field_id: field.field_id || 0,
            case_type_id: field.case_type_id || 0,
            required: field.required === 1 ? true : false,
            options: parsedOptions,
            obsolete: field.obsolete || 0,
            hidden: field.hidden || 0,
            add_item: field.add_item,
            default_value: field.default_value || "",
            display_when: field.display_when,
            is_answered: field.is_answered || "",
            is_answer_negated: false,
            section_id: parsedSectionId,
            template_id: parsedTemplateId
        });
        
        setEditingField(field);
        setCreateNew('field');
    };

    const handleFieldUpdate = async () => {
        const publicFormConfig = parsePublicFormRequestFieldConfig(newField.options);
        const isPublicFormRequest = isPublicFormRequestSelection;
        if (isPublicFormRequest && !publicFormConfig.form_id) {
            setFieldSaveError('Choose a published public form for this field.');
            return false;
        }
        if (isPublicFormRequest && publicFormConfig.default_channels.length === 0) {
            setFieldSaveError('Choose email, text message, or both.');
            return false;
        }
        setFieldSaveError('');
        try {
            const response = await fetch('https://api.casedb.co/custom_fields.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...newField,
                    ...(isPublicFormRequest ? {
                        required: false,
                        add_item: false,
                        default_value: '',
                        display_when: null,
                        is_answered: '',
                        is_answered_negated: false,
                        is_answer_negated: false,
                    } : {}),
                    options: isPublicFormRequest ? publicFormConfig : newField.options,
                    edit_field: true,
                    field_id: editingField.id,
                }),
                credentials: 'include'
            });

            const result = await response.json();
            
            if (result.success) {
                console.log('Field updated successfully');
                setCreateNew(null);
                resetForm();
                fetchAllData(); // Refresh the data
                return true;
            } else {
                console.error('Failed to update field:', result.message);
                setFieldSaveError(result.message || 'Failed to update custom field.');
            }
        } catch (error) {
            console.error('Error updating field:', error);
            setFieldSaveError(error.message || 'Failed to update custom field.');
        }
        return false;
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (hintRef.current && !hintRef.current.contains(event.target)) {
                setShowTooltip(false);
            }
        };

        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    })

    return (
        <>
            <div className={`firm-container ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`}>
                <div id='page-header' style={{ marginBottom: 0 }}>
                    <h2>Custom Fields</h2>
                    <button className='action' onClick={() => {
                        resetForm();
                        setCreateNew('field');
                    }}>New Field</button>
                    <button className='action alt' onClick={() => setCreateNew('case type')}>New Case Type</button>
                </div>
                <h4 style={{ margin: '0', color: 'var(--subtext-color)', display: 'flex', gap: '5px' }}>
                    Case Types
                    <div className='hint-container' ref={hintRef}>
                        <Info className='info-box' size={18} onMouseOver={() => setShowTooltip(true)} onMouseOut={() => setShowTooltip(false)}/>
                        {showTooltip && (
                            <div className='hint'>
                                Click on a case type to view its custom fields. Click again to hide them.
                            </div>
                        )}
                    </div>
                </h4>
                <div className='case-types'>
                    {caseTypes.filter(ct => Number(ct.id) === activeCaseType || !activeCaseType).map((type) => (
                        <div key={type.id} className='case-type' onClick={() => setActiveCaseType(prev => prev ? null : Number(type.id))}>
                            <p>{type.name} ({type.aka})</p>
                            {type.description && <p className='subtext'>{type.description}</p>}
                        </div>
                    ))}
                </div>
                <h4 className='sub-title alt'>Custom Fields</h4>
                <div className="fields-list">
                    {fields?.filter(field => field.case_type_id === activeCaseType || activeCaseType === null).map((field) => {
                        const answeredValue = (() => {
                            try {
                                const parsed = JSON.parse(field.is_answered);
                                return Number(parsed);
                            } catch {
                                return parseFloat(field.is_answered);
                            }
                        })();

                        return (
                            <div key={field.id} className="field">
                                <div className='field-header'>
                                    <p className='field-name'>{field.name}{" "}
                                        <span className='subtext'>Field ID: {field.id}</span>
                                        <span className='subtext'>
                                            ({fieldTypes?.map((fieldType, index) => 
                                                field.field_id === Number(fieldType.id) ? fieldType.name : null
                                            )})
                                        </span>
                                    </p>
                                </div>
                                <div className='form-group alt mid' style={{ maxWidth: '250px' }}>
                                    {(field.field_id === 1 || field.field_id === 2) && <Text type={field.field_id === 2 ? 'textarea' : 'text'} placeholder={field.name} value={fieldOptionsSelected[field.id] || ''} onChange={(e) => handleBooleanChange(field.id, e)}/>}
                                    {(field.field_id === 3 || field.field_id === 4 || field.field_id === 5) && <NumberInput type={field.field_id === 3 ? 'currency' : field.field_id === 4 ? 'percent' : 'number'} value={fieldOptionsSelected[field.id] || ''} onChange={(e) => handleBooleanChange(field.id, e)}/>}
                                    {field.field_id === 6  && <DateInput value={fieldOptionsSelected[field.id] || ''} onChange={(e) => handleBooleanChange(field.id, e)}/>}
                                    {field.field_id === 8 && <Contact  value={fieldOptionsSelected[field.id] || ''} setSelectedContact={(e) => handleBooleanChange(field.id, e)} onCreateNewContact={() => null}/>}
                                    {field.field_id === 10 && <Dropdown options={field.options} placeholder={field.name} value={fieldOptionsSelected[field.id] || ""} onChange={(e) => handleBooleanChange(field.id, e)}/>}
                                    {field.field_id === 12 && <Boolean options={field.options} value={fieldOptionsSelected[field.id] || 2} onChange={(e) => handleBooleanChange(field.id, e)} />}
                                    {field.field_id === 28 && <button type="button" className="action small quaternary" disabled><Send size={16} /> {parsePublicFormRequestFieldConfig(field.options).button_label}</button>}
                                </div>
                                <div className='field-info'>
                                    <p className='subtext display-rule'>
                                        {field.display_when ? (() => {
                                            // Handle display_when as array or single value
                                            let displayWhenValue = field.display_when;
                                            let parsedDisplayWhen = null;
                                            
                                            try {
                                                parsedDisplayWhen = JSON.parse(String(displayWhenValue));
                                            } catch (e) {
                                                parsedDisplayWhen = displayWhenValue;
                                            }
                                            
                                            // For array, show first field dependency
                                            let fieldIdToCheck = null;
                                            if (Array.isArray(parsedDisplayWhen)) {
                                                fieldIdToCheck = parsedDisplayWhen[0];
                                            } else {
                                                fieldIdToCheck = parseInt(parsedDisplayWhen);
                                            }
                                            
                                            const controllingField = fields.find(f =>
                                                f.id === fieldIdToCheck && f.case_type_id === field.case_type_id
                                            );

                                            if (!controllingField) return null;

                                            if (field.is_answered === '[!]') {
                                                return `Display when ${controllingField.name} is 'nothing'`;
                                            }

                                            let answeredValue;
                                            try {
                                                const parsed = JSON.parse(field.is_answered);
                                                answeredValue = Number(parsed);
                                            } catch {
                                                answeredValue = parseFloat(field.is_answered);
                                            }

                                            const optionLabel = controllingField.options?.[answeredValue] ?? 'Anything';

                                            return `Display when ${controllingField.name} is '${optionLabel}'`;
                                        })() : null}
                                    </p>
                                    <p className="subtext">
                                        {caseTypes.map((caseTypes, index) =>
                                            field.case_type_id === Number(caseTypes.id) ? caseTypes.name : null
                                        )}
                                    </p>
                                    <span className='edit-field' title="Edit Field" >
                                        <SquarePen
                                            className='subtext'
                                            size={18}
                                            onClick={() => handleEditField(field)}
                                        />
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {createNew === 'field' ? (
                    <Modal
                        onClose={() => {
                            setCreateNew(null);
                            setAdvancedOpen(false);
                            resetForm();
                        }}
                        title={editingField ? 'Edit Field' : 'Create New Field'}
                        header={
                            <div className='modal-header-actions'>
                                <button className='action alt' onClick={() => {
                                    resetForm();
                                    setCreateNew(null);
                                    setAdvancedOpen(false);
                                }}>Cancel</button>
                                <button className='action' onClick={async () => {
                                    if (editingField) {
                                        await handleFieldUpdate();
                                    } else {
                                        await handleFieldSubmit();
                                    }
                                }}>
                                    {editingField ? 'Update' : 'Create'}
                                </button>
                            </div>
                        }
                    >
                        <div className='modal-content-wrapper' style={{ gap: '15px' }}>
                            <div className='field-details'>
                                <div className="form-group">
                                    <label htmlFor="name">Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={newField.name}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="field_id">Type</label>
                                    <select
                                        name="field_id"
                                        value={newField.field_id}
                                        onChange={handleInputChange}
                                        className='default-select'
                                    >
                                        <option value="">Select Type</option>
                                        {fieldTypes.map((type) => (
                                            <option key={type.id} value={type.id}>{type.name}</option>
                                        ))}
                                    </select>
                                </div>
                                {isPublicFormRequestFieldType(selectedFieldType) && (
                                    <PublicFormRequestFieldSettings
                                        value={newField.options}
                                        caseTypeId={newField.case_type_id}
                                        onChange={(options) => {
                                            setNewField((current) => ({ ...current, options }));
                                            setFieldSaveError('');
                                        }}
                                    />
                                )}
                                {fieldTypeNeedsOptions(selectedFieldType) && (
                                    <div className="form-group dropdown-list" style={{alignItems: "center"}}>
                                        <label>Options (Dropdown Choices)</label>
                                        {newField.options.map((option, index) => (
                                            <div key={index} className="option-input">
                                                <input
                                                    type="text"
                                                    value={option}
                                                    onChange={(e) => handleOptionChange(index, e.target.value)}
                                                />
                                                <button type="button" className='form-box alt' onClick={() => handleRemoveOption(index)}>-</button>
                                            </div>
                                        ))}
                                        <button type="button" className='form-box alt' onClick={handleAddOption}>+</button>
                                    </div>
                                )}
                                {isBooleanType(selectedFieldType) && (
                                    <div className="form-group list">
                                        <label>Options (Yes/No/Unknown - Auto-filled)</label>
                                        <ul>
                                            <li>Yes</li>
                                            <li>No</li>
                                            <li>Unknown</li>
                                        </ul>
                                    </div>
                                )}
                                <div className="form-group">
                                    <label htmlFor='case_type_id' className='info-label'>
                                        Case Type
                                    </label>
                                    <select
                                        name='case_type_id'
                                        value={newField.case_type_id}
                                        onChange={handleInputChange}
                                        className='default-select'
                                    >
                                        <option value="">Select Case Type</option>
                                        {caseTypes.map((type) => (
                                            <option key={type.id} value={type.id}>{type.name}</option>
                                        ))}
                                    </select>
                                </div>
                                {fieldSaveError && <div className="form-error" role="alert">{fieldSaveError}</div>}
                            </div>
                            <div className={`advanced-options ${advancedOpen ? 'open' : ''}`} onClick={() => setAdvancedOpen(prev => !prev)}>Advanced Options {advancedOpen ? <ChevronUp /> : <ChevronDown />}</div>
                            {advancedOpen && (
                                <div className='advanced-options-list'>
                                    {!isPublicFormRequestSelection && (
                                        <Checkbox
                                            value={newField.required}
                                            label="Required"
                                            onChange={() => setNewField({...newField, required: !newField.required})}
                                        />
                                    )}
                                    <Checkbox
                                        value={newField.obsolete}
                                        label="Obsolete"
                                        onChange={() => setNewField({...newField, obsolete: !newField.obsolete})}
                                    />
                                    <Checkbox
                                        value={newField.hidden}
                                        label="Hidden"
                                        onChange={() => setNewField({...newField, hidden: !newField.hidden})}
                                    />
                                    {!isPublicFormRequestSelection && (
                                        <>
                                            <Checkbox
                                                value={newField.add_item}
                                                label="Add Item"
                                                onChange={() => setNewField({...newField, add_item: !newField.add_item})}
                                            />
                                            <div className="form-group">
                                                <label htmlFor='default_value'>Default Value</label>
                                                {Array.isArray(newField.options) && newField.options.length > 0 ? (
                                                    <select
                                                        name='default_value'
                                                        value={newField.default_value}
                                                        onChange={handleInputChange}
                                                        className='default-select'
                                                    >
                                                        <option value=''>Select Default Value</option>
                                                        {newField.options.map((option, index) => (
                                                            <option key={index} value={option}>{option}</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <input
                                                        type='text'
                                                        name='default_value'
                                                        placeholder='Default value...'
                                                        value={newField.default_value}
                                                        onChange={handleInputChange}
                                                    />
                                                )}
                                            </div>
                                        </>
                                    )}
                                    {!isPublicFormRequestSelection && (
                                        <>
                                            <div className='form-group'>
                                                <label htmlFor='description'>Display This Field When</label>
                                                <select
                                                    name="display_when"
                                                    value={newField.display_when || ""}
                                                    onChange={handleInputChange}
                                                    className="default-select"
                                                >
                                                    <option value="" disabled>Select Field</option>
                                                    {filteredFields.map((f) => (
                                                        <option key={f.id} value={f.id}>{f.name.length > 50 ? `${f.name.slice(0, 50)}...` : f.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className='form-group'>
                                                <label
                                                    htmlFor='is_answered'
                                                    onClick={() => setNewField(prev => ({
                                                        ...prev,
                                                        is_answered_negated: !prev?.is_answered_negated
                                                    }))}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {newField.is_answered_negated ? "Is Not Answered As" : "Is Answered As"}
                                                </label>
                                                <select
                                                    name="is_answered"
                                                    multiple
                                                    size={8}
                                                    value={
                                                        Array.isArray(newField.is_answered)
                                                            ? newField.is_answered.map(v => String(v).replace(/^!/, ''))
                                                            : [newField.is_answered].filter(Boolean).map(v => String(v).replace(/^!/, ''))
                                                    }
                                                    onChange={(e) => {
                                                        const selectedOptions = Array.from(e.target.selectedOptions, (opt) => {
                                                            return newField?.is_answered_negated ? `!${opt.value}` : opt.value;
                                                        });
                                                        handleInputChange({
                                                            target: {
                                                                name: "is_answered",
                                                                value: selectedOptions
                                                            }
                                                        });
                                                    }}
                                                    className="default-select multiple-select"
                                                    style={{ height: "auto", overflow: 'auto !important', flexGrow: "1 !important", minHeight: "40px" }}
                                                >
                                                    {displayFieldOptions.map((option, index) => (
                                                        <option key={index} value={String(index)}>{option}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </>
                                    )}
                                    <div className="form-group">
                                        <label htmlFor='template_id'>Template ID</label>
                                        <select
                                            name='template_id'
                                            value={newField.template_id}
                                            onChange={handleInputChange}
                                            className='default-select'
                                        >
                                            <option value="">Select Template</option>
                                            {templates.map((template) => (
                                                <option key={template.id} value={template.id}>{template.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor='section_id'>Section ID</label>
                                        <select
                                            name='section_id'
                                            value={newField.section_id}
                                            onChange={handleInputChange}
                                            className='default-select'
                                        >
                                            <option value="">Select Section</option>
                                            {sections.map((section) => (
                                                <option key={section.id} value={section.id}>{section.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Modal>
                ) : createNew === 'case type' && (
                    <Modal onClose={() => setCreateNew(null)}>
                        <div className='modal-content-wrapper'>
                            <div className='new-template'>
                                <h4>Create New Case Type</h4>
                                <div className="form-group">
                                    <label htmlFor="name">Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={newField.name}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className='form-group'>
                                    <label htmlFor='description'>Description</label>
                                    <textarea
                                        name='description'
                                        value={newField.description}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <button className='action' onClick={() => {createCaseType(); setCreateNew(null);}}>Create</button>
                            </div>
                        </div>
                    </Modal>
                )}
            </div>
        </>
    );
}
